import {
  DefaultToastOptions, Renderable, toast as hotToast, Toast, ToastBar, Toaster as HotToaster,
  ToastOptions, ToastType, useToaster, useToasterStore, ValueOrFunction
} from 'react-hot-toast';
import sanitize from 'sanitize-html';
import Portal from '@/components/Portal';
import {typographyStyle} from '@/components/Typography';
import {LAYOUT_SIZE, Z_INDEX} from '@/styles';
import {palette} from '@/styles/palette';
import {replaceLineBreak} from '@/utils';

type Message = ValueOrFunction<Renderable, Toast>;

type ToastHandler = (message: Message, options?: ToastOptions) => string;

const createHandler =
  (type?: ToastType): ToastHandler =>
  (message, options) => {
    if (typeof message === 'string') {
      message = (
        <div
          dangerouslySetInnerHTML={{
            __html: sanitize(replaceLineBreak(message)),
          }}
        />
      );
    }

    if (!type || type === 'blank') {
      return hotToast(message, options);
    }

    return hotToast[type](message, options);
  };

const toast = (message: Message, opts?: ToastOptions) =>
  createHandler('blank')(message, opts);

toast.error = createHandler('error');
toast.success = createHandler('success');
toast.loading = createHandler('loading');
toast.custom = createHandler('custom');
toast.dismiss = hotToast.dismiss;
toast.promise = hotToast.promise;
toast.remove = hotToast.remove;

const hotToastStyle: DefaultToastOptions = {
  style: {
    fontFamily: 'Pretendard',
    borderRadius: '4px',
    border: `solid 1px ${palette.gray200}`,
    color: palette.textPrimary,
    padding: '0 14px',
    fontWeight: 500,
    minHeight: 48,
    maxWidth: 520,
    boxShadow: 'none',
    textAlign: 'center',
    ...typographyStyle.medium14,
  },
  success: {
    style: {
      background: '#ECFBFA',
      borderColor: '#A8DCD9',
      color: palette.teal200,
      textAlign: 'left',
    },
    iconTheme: {
      primary: '#ECFBFA',
      secondary: palette.teal200,
    },
  },
  error: {
    icon: 'ⓘ',
    style: {
      background: '#FFF4F3',
      borderColor: '#FFDDD8',
      color: palette.red100,
      textAlign: 'left',
    },
    iconTheme: {
      primary: '#FFF4F3',
      secondary: palette.red100,
    },
  },
};

const Toaster = () => {
  return (
    <Portal elementId="toast-portal">
      <HotToaster
        containerStyle={{
          zIndex: Z_INDEX.snackbar,
          top: LAYOUT_SIZE.NAV_HEIGHT + 16,
        }}
        toastOptions={hotToastStyle}
      />
    </Portal>
  );
};

export {Toaster, useToaster, useToasterStore, ToastBar, toast};
export default toast;
